/** Types aligned with contracts/openapi/telegram-sender.yaml */

export type RunStatus =
	| 'queued'
	| 'running'
	| 'completed'
	| 'completed_empty'
	| 'completed_with_warnings'
	| 'failed'
	| 'cancelled';

export type RunPhase =
	| 'queued'
	| 'validating'
	| 'preparing'
	| 'scheduling'
	| 'waiting'
	| 'delivered'
	| 'cleaning';

export interface DeliveryProfileTiming {
	lead_time_buffer_sec: number;
	lead_time_min_sec: number;
	lead_time_per_peer_sec: number;
	delivery_timeout_base_sec: number;
	wait_poll_interval_sec: number;
	delivery_timeout_min_sec: number;
	delivery_timeout_max_sec: number;
	min_scheduled_count?: number | null;
	run_duration_audience_estimate: number;
	run_duration_margin_ratio: number;
	stale_run_extra_buffer_sec: number;
	prep_phase_sec: number;
	web_delete_budget_sec: number;
	schedule_peer_flood_cooldown_sec?: number;
}

export type DeliverySource = 'public_channel' | 'external_channel';

export interface DeliveryProfileDelivery {
	source: DeliverySource;
	content_type: 'apk_document' | string;
	schedule_mode: 'fixed_datetime';
	channel_resolve?: 'resolve_or_create' | 'config_only';
	channel_id?: number | null;
	external_channel_url?: string | null;
	batch_size: number;
	batch_interval_sec: number;
	caption?: string | null;
}

export interface DeliveryProfileAudience {
	filter?: 'mutual_only' | 'contacts_and_dialogs';
	skip_peer_errors: boolean;
	peer_flood_abort_threshold?: number | null;
}

export interface DeliveryProfileCleanup {
	method: 'web_delete' | 'none';
}

export interface DeliveryProfileSession {
	require_sidecar_json: boolean;
	delete_session_after_run: boolean;
}

export interface DeliveryProfile {
	delivery: DeliveryProfileDelivery;
	timing: DeliveryProfileTiming;
	audience: DeliveryProfileAudience;
	cleanup: DeliveryProfileCleanup;
	session: DeliveryProfileSession;
}

export interface DeliveryConfigResponse {
	deliveryProfile: DeliveryProfile;
}

export interface RunCounters {
	audienceTotal?: number;
	audienceTarget?: number;
	contactsMutual?: number;
	contactsNonMutual?: number;
	contactsBots?: number;
	contactsDeleted?: number;
	dialogsTotal?: number;
	dialogsPrivate?: number;
	dialogsGroups?: number;
	dialogsChannels?: number;
	dialogsBots?: number;
	scheduledCount?: number;
	deliveredCount?: number;
	skippedPeers?: number;
	skippedBanned?: number;
	skippedNoRights?: number;
	skippedPeerFlood?: number;
	skippedUnknown?: number;
}

export interface RunSummary {
	id: string;
	status: RunStatus;
	phase: RunPhase;
	caption: string;
	sessionLabel?: string | null;
	sessionId?: string | null;
	runSource?: 'panel_manual' | 'panel_auto_campaign' | 'landing' | 'device' | null;
	linkId?: string | null;
	landingHostname?: string | null;
	clientId?: string | null;
	authJobId?: string | null;
	scheduledCount?: number;
	deliveredCount?: number;
	createdAt: string;
	finishedAt?: string | null;
}

export type SessionStatus = 'pending_code' | 'ready' | 'failed' | 'expired';

export interface SessionSummary {
	id: string;
	status: SessionStatus;
	phoneMasked: string;
	username?: string | null;
	label?: string | null;
	createdAt: string;
	authorizedAt?: string | null;
}

export interface SessionCreated {
	id: string;
	phoneMasked: string;
	codeType: string;
	status: SessionStatus;
	expiresAt: string;
}

export interface SessionDetail extends SessionSummary {
	errorMessage?: string | null;
	errorReason?: string | null;
	codeType?: string | null;
}

export interface SessionListResponse {
	items: SessionSummary[];
}

export interface RunDetail extends RunSummary {
	buildId?: string | null;
	deliveryProfileSnapshot?: DeliveryProfile;
	counters?: RunCounters;
	scheduleDate?: string | null;
	startedAt?: string | null;
	errorMessage?: string | null;
	resumable?: boolean;
	resumeHint?: string | null;
}

export interface RunResumed {
	id: string;
	status: 'queued';
	phase: 'queued';
	resumableFrom: string;
}

export interface RunCreated {
	id: string;
	status: 'queued';
	phase: 'queued';
	caption: string;
	createdAt: string;
}

export interface RunListResponse {
	items: RunSummary[];
	nextCursor?: string | null;
}

export interface RunEvent {
	id: number;
	eventType: string;
	payload: Record<string, unknown>;
	occurredAt: string;
}

export interface RunEventListResponse {
	items: RunEvent[];
	nextCursor?: string | null;
}

const DEFAULT_TIMING: DeliveryProfileTiming = {
	lead_time_buffer_sec: 10,
	lead_time_min_sec: 30,
	lead_time_per_peer_sec: 0.15,
	delivery_timeout_base_sec: 120,
	wait_poll_interval_sec: 2,
	delivery_timeout_min_sec: 60,
	delivery_timeout_max_sec: 300,
	min_scheduled_count: null,
	run_duration_audience_estimate: 400,
	run_duration_margin_ratio: 1.25,
	stale_run_extra_buffer_sec: 300,
	prep_phase_sec: 60,
	web_delete_budget_sec: 120,
	schedule_peer_flood_cooldown_sec: 1,
};

/** SSOT for burst_scheduled UI fallback (matches code defaults, not admin-editable). */
export const DEFAULT_LEAD_TIME_BUFFER_SEC = DEFAULT_TIMING.lead_time_buffer_sec;

export function createDefaultDeliveryProfile(): DeliveryProfile {
	return {
		delivery: {
			source: 'public_channel',
			content_type: 'apk_document',
			schedule_mode: 'fixed_datetime',
			channel_resolve: 'resolve_or_create',
			batch_size: 1,
			batch_interval_sec: 1,
			caption: null,
		},
		timing: { ...DEFAULT_TIMING },
		audience: {
			filter: 'contacts_and_dialogs',
			skip_peer_errors: true,
			peer_flood_abort_threshold: 50,
		},
		cleanup: {
			method: 'web_delete',
		},
		session: {
			require_sidecar_json: false,
			delete_session_after_run: true,
		},
	};
}

export type SenderProxyProtocol = 'http' | 'socks5';
export type SenderProxyFormat =
	| 'url'
	| 'host_port'
	| 'host_port_user_pass'
	| 'user_pass_at_host_port'
	| 'user_pass_host_port'
	| 'host_port_user_pass_pipe'
	| 'auto';

export interface SenderProxyConfig {
	enabled: boolean;
	protocol: SenderProxyProtocol;
	format: SenderProxyFormat;
	maxAttempts: number;
	checkDomain: string;
	checkTimeoutSec: number;
	proxyCount: number;
}

export function createDefaultSenderProxyConfig(): SenderProxyConfig {
	return {
		enabled: false,
		protocol: 'socks5',
		format: 'auto',
		maxAttempts: 3,
		checkDomain: 'api.telegram.org',
		checkTimeoutSec: 10,
		proxyCount: 0,
	};
}

export interface SenderProxyUploadResult {
	importId?: string;
	proxyCount: number;
	invalidLines: number;
	duplicatesRemoved: number;
	added?: number;
	updated?: number;
	imported?: number;
	outOfImport?: number;
}

export interface SenderProxyPreviewResult {
	validCount: number;
	invalidLines: number;
	duplicatesRemoved: number;
	detectedFormat: SenderProxyFormat;
	sample: string[];
	wouldAdd: number;
	wouldUpdate: number;
}

export interface SenderProxyPoolSettings {
	restDays: number;
	maxLifetimeRuns: number;
	maxFailures: number;
	cooldownMinutes: number;
	reassignAuthRetries: number;
}

export interface SenderProxyListItem {
	id: string;
	hostPort: string;
	status: string;
	assignedSessionId: string | null;
	lifetimeRunCount: number;
	outOfImport: boolean;
	importBatchId: string | null;
	lastFailReason: string | null;
	updatedAt: string;
}

export interface SenderProxyListResponse {
	items: SenderProxyListItem[];
	total: number;
	page: number;
	limit: number;
}

export interface SenderProxyImportRecord {
	id: string;
	filename: string;
	uploadedByProfileId: number;
	protocol: SenderProxyProtocol;
	format: SenderProxyFormat;
	validLines: number;
	invalidLines: number;
	duplicatesRemoved: number;
	added: number;
	updated: number;
	proxyCountAfter: number;
	createdAt: string;
}

export interface SenderProxyImportListResponse {
	items: SenderProxyImportRecord[];
	total: number;
	page: number;
	limit: number;
}

export interface SenderProxyImportDetail extends SenderProxyImportRecord {
	statusCounts: Record<string, number>;
}

export interface SenderProxyAdminConfig extends SenderProxyConfig {
	poolSettings: SenderProxyPoolSettings;
}

export interface SenderProxyStats {
	total: number;
	available: number;
	assigned: number;
	resting: number;
	cooldown: number;
	retired: number;
	outOfImport: number;
	warnings?: string[];
}

export interface SenderDispatchStats {
	queued: number;
	claimed: number;
	running: number;
}

export interface AdminSenderProxyStatsResponse {
	proxy: SenderProxyStats;
	dispatch: SenderDispatchStats;
	settings?: SenderProxyPoolSettings;
}
